import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Zap, UploadCloud, Shield, CheckCircle, XCircle,
    ArrowRight, ChevronDown, FileText, Globe,
    AlertTriangle, Layers, Search, ExternalLink, Star, LayoutDashboard, BarChart2,
    ShieldAlert, TrendingDown, ScanLine,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LogoIcon from '../components/LogoIcon';

// ── Tiny helpers ─────────────────────────────────────────────────────────────

function FadeIn({ children, delay = 0, className = '' }: {
    children: React.ReactNode; delay?: number; className?: string;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { threshold: 0.1 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ${className} ${visible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
}

// ── Mock: 供应链货源对比演示卡（Hero 右侧）──────────────────────────────────

type OriginRate = {
    code: string;
    flag: string;
    label: string;
    rate: number;
    risk: 'danger' | 'warning' | 'safe';
    fta?: string;
};

type MockScanItem = {
    sku: string;
    desc: string;
    hts: string;
    cost: number;
    origins: OriginRate[];
};

const mockScanItems: MockScanItem[] = [
    {
        sku: 'SKU-001',
        desc: 'Wireless Bluetooth Headphones ANC',
        hts: '8518.30',
        cost: 15,
        origins: [
            { code: 'CN', flag: '🇨🇳', label: '中国',   rate: 145, risk: 'danger' },
            { code: 'VN', flag: '🇻🇳', label: '越南',   rate: 7.5, risk: 'safe' },
            { code: 'MX', flag: '🇲🇽', label: '墨西哥', rate: 0,   risk: 'safe', fta: 'USMCA' },
        ],
    },
    {
        sku: 'SKU-002',
        desc: 'Stainless Steel Water Bottle 32oz',
        hts: '7323.93',
        cost: 8,
        origins: [
            { code: 'CN', flag: '🇨🇳', label: '中国',   rate: 52.5, risk: 'warning' },
            { code: 'VN', flag: '🇻🇳', label: '越南',   rate: 3.5,  risk: 'safe' },
            { code: 'MX', flag: '🇲🇽', label: '墨西哥', rate: 0,    risk: 'safe', fta: 'USMCA' },
        ],
    },
    {
        sku: 'SKU-003',
        desc: 'Cotton Canvas Tote Bag',
        hts: '4202.92',
        cost: 5,
        origins: [
            { code: 'CN', flag: '🇨🇳', label: '中国',   rate: 7.5,  risk: 'safe' },
            { code: 'VN', flag: '🇻🇳', label: '越南',   rate: 12.5, risk: 'warning' },
            { code: 'MX', flag: '🇲🇽', label: '墨西哥', rate: 0,    risk: 'safe', fta: 'USMCA' },
        ],
    },
];

const RISK_COLOR  = { danger: 'text-red-400',    warning: 'text-amber-400',    safe: 'text-emerald-400' };
const RISK_BG     = { danger: 'bg-red-500/10 border-red-500/20', warning: 'bg-amber-500/10 border-amber-500/20', safe: 'bg-emerald-500/10 border-emerald-500/20' };
const RISK_DOT    = { danger: 'bg-red-400',       warning: 'bg-amber-400',       safe: 'bg-emerald-400' };
const RISK_LABEL  = { danger: '高风险',            warning: '注意',               safe: '较安全' };

function MockRiskScanCard() {
    const [expanded, setExpanded] = useState<number | null>(0);

    function getLanded(cost: number, rate: number) { return cost * (1 + rate / 100); }

    return (
        <div className="glass rounded-2xl overflow-hidden shadow-2xl shadow-black/40 glow">
            {/* Header */}
            <div className="bg-surface-2/60 px-4 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                    <Globe size={14} className="text-brand-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm font-medium flex-1">供应链货源对比分析</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-300 flex-shrink-0">
                        3 国对比
                    </span>
                </div>
            </div>

            {/* Summary row */}
            <div className="grid grid-cols-3 divide-x divide-white/[0.06] border-b border-white/[0.06]">
                {[
                    { label: '当前来源', val: '🇨🇳 中国', color: 'text-red-400' },
                    { label: '可选来源', val: '越南 · 墨西哥', color: 'text-brand-300' },
                    { label: '最大节省', val: '$21.75/件', color: 'text-emerald-400' },
                ].map((s) => (
                    <div key={s.label} className="py-2.5 text-center">
                        <div className={`text-xs font-bold ${s.color}`}>{s.val}</div>
                        <div className="text-[10px] text-slate-500">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Scan items */}
            <div className="divide-y divide-white/[0.04]">
                {mockScanItems.map((item, i) => {
                    const cnOrig   = item.origins.find(o => o.code === 'CN')!;
                    const bestOrig = [...item.origins].sort((a, b) => a.rate - b.rate)[0];
                    const saving   = getLanded(item.cost, cnOrig.rate) - getLanded(item.cost, bestOrig.rate);

                    return (
                        <div key={item.sku} className="px-4 py-3">
                            {/* Collapsed row */}
                            <div
                                className="flex items-center gap-2.5 cursor-pointer"
                                onClick={() => setExpanded(expanded === i ? null : i)}
                            >
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${RISK_DOT[cnOrig.risk]}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-slate-500">{item.sku}</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${RISK_BG[cnOrig.risk]} ${RISK_COLOR[cnOrig.risk]}`}>
                                            {cnOrig.flag} {cnOrig.rate}%
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-300 truncate mt-0.5">{item.desc}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    {saving > 0.5 ? (
                                        <div className="text-emerald-400 text-xs font-semibold leading-tight">
                                            {bestOrig.flag} {bestOrig.rate}%
                                        </div>
                                    ) : (
                                        <div className={`text-sm font-bold ${RISK_COLOR[cnOrig.risk]}`}>{cnOrig.rate}%</div>
                                    )}
                                    {saving > 0.5
                                        ? <div className="text-[10px] text-emerald-400">省 ${saving.toFixed(2)}/件</div>
                                        : <div className="text-[10px] text-slate-600">对华税率</div>
                                    }
                                </div>
                            </div>

                            {/* Expanded: multi-origin comparison table */}
                            {expanded === i && (
                                <div className="mt-2.5 rounded-xl overflow-hidden border border-white/[0.08] text-xs">
                                    {/* Country header row */}
                                    <div className="grid border-b border-white/[0.06] bg-surface-2/60"
                                         style={{ gridTemplateColumns: '72px repeat(3, 1fr)' }}>
                                        <div className="px-2 py-1.5 text-slate-600 text-[10px] font-mono">{item.hts}</div>
                                        {item.origins.map(o => (
                                            <div key={o.code} className="px-1 py-1.5 text-center text-[10px] text-slate-400">
                                                {o.flag} {o.label}
                                                {o.fta && <span className="block text-emerald-500 text-[8px]">{o.fta}</span>}
                                            </div>
                                        ))}
                                    </div>
                                    {/* Tax rate row */}
                                    <div className="grid border-b border-white/[0.04]"
                                         style={{ gridTemplateColumns: '72px repeat(3, 1fr)' }}>
                                        <div className="px-2 py-1.5 text-slate-500">税率</div>
                                        {item.origins.map(o => (
                                            <div key={o.code} className={`px-1 py-1.5 text-center font-bold ${RISK_COLOR[o.risk]}`}>
                                                {o.rate}%
                                            </div>
                                        ))}
                                    </div>
                                    {/* Landed cost row */}
                                    <div className="grid" style={{ gridTemplateColumns: '72px repeat(3, 1fr)' }}>
                                        <div className="px-2 py-1.5 text-slate-500">含税到岸</div>
                                        {item.origins.map(o => {
                                            const landed = getLanded(item.cost, o.rate);
                                            const isBest = o.code === bestOrig.code;
                                            return (
                                                <div key={o.code}
                                                     className={`px-1 py-1.5 text-center ${isBest ? 'text-emerald-400 font-semibold' : 'text-slate-400'}`}>
                                                    ${landed.toFixed(2)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Saving hint */}
                                    {saving > 0.5 && (
                                        <div className="px-3 py-1.5 bg-emerald-500/5 border-t border-emerald-500/10 text-[10px] text-emerald-400 flex items-center gap-1">
                                            <TrendingDown size={9} />
                                            迁至{bestOrig.label}（{bestOrig.flag}）→ 节省 ${saving.toFixed(2)}/件（进货 ${item.cost}）
                                        </div>
                                    )}
                                    {/* VN > CN hint for SKU-003 */}
                                    {!saving && (
                                        <div className="px-3 py-1.5 bg-amber-500/5 border-t border-amber-500/10 text-[10px] text-amber-400 flex items-center gap-1">
                                            <AlertTriangle size={9} />
                                            越南税率反高于中国 — 此品类墨西哥（USMCA 0%）更划算
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-surface-2/40 border-t border-white/[0.06]">
                <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>3 个 SKU · 迁至墨西哥最大节省 $21.75/件</span>
                    <span className="text-brand-400 flex items-center gap-1">
                        导出迁移报告 <ExternalLink size={10} />
                    </span>
                </div>
            </div>
        </div>
    );
}

// ── Section: Navbar ───────────────────────────────────────────────────────────
function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const { isAuthenticated, user } = useAuth();

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handler);
        return () => window.removeEventListener('scroll', handler);
    }, []);

    return (
        <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-surface-1/90 backdrop-blur-xl border-b border-white/[0.06]' : ''}`}>
            <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <LogoIcon size={32} />
                    <span className="text-sm font-bold text-white tracking-tight">税码通</span>
                </div>
                <nav className="hidden md:flex items-center gap-6 text-sm text-slate-400">
                    <a href="#risk-scan" className="hover:text-white transition-colors flex items-center gap-1">
                        <ShieldAlert size={13} className="text-red-400" />
                        风险扫描
                    </a>
                    <a href="#features" className="hover:text-white transition-colors">功能</a>
                    <a href="#compare" className="hover:text-white transition-colors">竞品对比</a>
                    <a href="#pricing" className="hover:text-white transition-colors">定价</a>
                    <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
                </nav>
                <div className="flex items-center gap-3">
                    {isAuthenticated ? (
                        <>
                            <span className="hidden sm:block text-xs text-slate-500">
                                {user?.name || user?.email}
                            </span>
                            <Link to="/dashboard" className="btn-primary py-2 text-sm">
                                <LayoutDashboard size={14} />
                                进入控制台
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors px-3 py-1.5">
                                登录
                            </Link>
                            <Link to="/register" className="btn-primary py-2 text-sm">
                                免费开始
                                <ArrowRight size={14} />
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}

// ── Section: Hero ─────────────────────────────────────────────────────────────
function Hero() {
    const { isAuthenticated } = useAuth();
    return (
        <section className="relative min-h-screen flex flex-col items-center justify-center pt-20 pb-16 px-6 overflow-hidden">
            {/* Background orbs */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-brand-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] bg-red-700/6 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative max-w-6xl w-full mx-auto">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    {/* Left: copy */}
                    <div className="flex-1 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-300 text-xs font-medium mb-6">
                            <Globe size={11} />
                            🇨🇳 中国 145% → 🇻🇳 越南 7.5% → 🇲🇽 墨西哥 0%（USMCA）— 哪国货源最划算？
                        </div>

                        <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.12] mb-6">
                            多国货源关税
                            <br />
                            <span className="text-gradient">一键对比决策</span>
                            <br />
                            <span className="text-slate-400 text-3xl lg:text-4xl font-normal">+ 含税利润 · 迁产方案即时可见</span>
                        </h1>

                        <p className="text-slate-400 text-base lg:text-lg leading-relaxed mb-8 max-w-xl lg:max-w-none">
                            上传 SKU 清单，AI 自动匹配 HTS 编码，同步对比中国、越南、墨西哥、印度四国关税，
                            直接算出每个来源的含税到岸成本和目标售价。迁产前先算清楚，不靠拍脑袋。
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                            {isAuthenticated ? (
                                <>
                                    <Link to="/scan" className="btn-primary text-base px-6 py-3">
                                        <Globe size={17} />
                                        立即对比多国货源
                                    </Link>
                                    <Link to="/dashboard" className="btn-secondary text-base px-6 py-3">
                                        <LayoutDashboard size={16} />
                                        进入控制台
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link to="/register" className="btn-primary text-base px-6 py-3">
                                        <Globe size={17} />
                                        免费对比货源税率
                                    </Link>
                                    <a href="#risk-scan" className="btn-secondary text-base px-6 py-3">
                                        查看功能详情
                                        <ChevronDown size={16} />
                                    </a>
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-5 mt-8 justify-center lg:justify-start">
                            {[
                                { icon: CheckCircle, text: '无需信用卡', color: 'text-emerald-400' },
                                { icon: CheckCircle, text: '4 国税率实时数据', color: 'text-emerald-400' },
                                { icon: CheckCircle, text: '含税利润自动测算', color: 'text-emerald-400' },
                            ].map(({ icon: Icon, text, color }) => (
                                <div key={text} className={`flex items-center gap-1.5 text-xs ${color}`}>
                                    <Icon size={12} />
                                    <span className="text-slate-400">{text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: risk scan mock */}
                    <div className="flex-1 w-full max-w-lg" id="demo">
                        <MockRiskScanCard />
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <a href="#stats" className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600 hover:text-slate-400 transition-colors animate-bounce">
                <span className="text-xs">向下了解</span>
                <ChevronDown size={18} />
            </a>
        </section>
    );
}

// ── Section: 2025 tariff banner ────────────────────────────────────────────────
function TariffBanner() {
    const items = [
        '🌏 多国货源对比：🇨🇳 中国 145% vs 🇻🇳 越南 7.5% vs 🇲🇽 墨西哥 0%（USMCA）vs 🇮🇳 印度 — 迁产前先算清楚',
        '📦 对华综合税率最高达 145% · 行政令持续生效',
        '⚡ Section 301 关税：7.5% – 25%（按章节）',
        '🚨 行政令附加：20% 芬太尼EO + 125% 互惠关税',
        '✅ 部分电子品豁免：8471 / 8473 / 8517 / 8528 等',
        '💰 CSV 含进货成本 → 多来源含税利润一键对比，迁产决策有数据支撑',
        '📋 HTS 编码直接决定税率 — 错码风险：补税 + 滞港',
    ];

    return (
        <div className="bg-amber-950/30 border-y border-amber-500/15 overflow-hidden py-2.5">
            <div className="flex gap-16 animate-[ticker_30s_linear_infinite] whitespace-nowrap">
                {[...items, ...items].map((item, i) => (
                    <span key={i} className="text-xs text-amber-400/80 font-medium flex-shrink-0">{item}</span>
                ))}
            </div>
        </div>
    );
}

// ── Section: Stats ─────────────────────────────────────────────────────────────
function Stats() {
    const stats = [
        { value: '4 国', label: '货源国税率实时覆盖', sub: '中国 · 越南 · 墨西哥 · 印度' },
        { value: '≥92%', label: '高置信度分类占比', sub: '置信度 ≥80%，附 GRI 依据' },
        { value: '1,000', label: '单批次最大 SKU 数', sub: 'CSV 一键上传，多来源并排对比' },
        { value: '< 2min', label: 'CSV → 迁产决策报告', sub: 'HTS + 多国税率 + 利润全流程' },
    ];

    return (
        <section id="stats" className="py-16 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {stats.map((s, i) => (
                        <FadeIn key={s.label} delay={i * 80}>
                            <div className="glass rounded-2xl p-6 text-center">
                                <div className="text-3xl font-bold text-gradient mb-1">{s.value}</div>
                                <div className="text-sm text-white font-medium mb-1">{s.label}</div>
                                <div className="text-xs text-slate-500">{s.sub}</div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Section: Risk Scan Showcase ───────────────────────────────────────────────
function RiskScanShowcase() {
    const { isAuthenticated } = useAuth();

    const highlights = [
        {
            icon: Globe,
            title: '多国货源税率一键对比',
            desc: '同一个 SKU，同时显示中国、越南、墨西哥、印度的进口关税。哪国最低、省多少钱，一屏看清，无需逐国手动查。',
            color: 'text-brand-400',
            bg: 'bg-brand-500/10',
        },
        {
            icon: TrendingDown,
            title: '迁产节省金额即时测算',
            desc: '输入进货成本，自动算出各来源国的含税到岸成本差值。「迁到墨西哥每件节省 $21.75」这类结论直接给你，不用自己算。',
            color: 'text-emerald-400',
            bg: 'bg-emerald-500/10',
        },
        {
            icon: BarChart2,
            title: '关税风险识别 + 利润报告',
            desc: '高/中/低风险三级标注，含税利润汇总，高风险 SKU 推荐低税率替代来源，一键导出 CSV/PDF，直接对接采购决策流程。',
            color: 'text-red-400',
            bg: 'bg-red-500/10',
        },
    ];

    return (
        <section className="py-20 px-6 bg-surface-1/50" id="risk-scan">
            <div className="max-w-6xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs mb-4">
                            <Globe size={11} /> 核心差异化功能
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-3">供应链货源对比 + 关税风险扫描</h2>
                        <p className="text-slate-400 max-w-xl mx-auto">
                            不只告诉你中国关税是多少——更告诉你<strong className="text-white">换到越南/墨西哥能省多少、利润变化几何、值不值得迁</strong>
                        </p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    {/* Left: highlights */}
                    <div className="space-y-5">
                        {highlights.map((h, i) => (
                            <FadeIn key={h.title} delay={i * 100}>
                                <div className="glass rounded-2xl p-5 flex gap-4">
                                    <div className={`w-10 h-10 rounded-xl ${h.bg} flex items-center justify-center flex-shrink-0`}>
                                        <h.icon size={18} className={h.color} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-white mb-1">{h.title}</h3>
                                        <p className="text-sm text-slate-400 leading-relaxed">{h.desc}</p>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}

                        <FadeIn delay={350}>
                            <div className="pt-2">
                                {isAuthenticated ? (
                                    <Link to="/scan" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2">
                                        <Globe size={16} />
                                        立即对比多国货源
                                        <ArrowRight size={14} />
                                    </Link>
                                ) : (
                                    <Link to="/register" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2">
                                        <Globe size={16} />
                                        免费注册，立即对比货源
                                        <ArrowRight size={14} />
                                    </Link>
                                )}
                            </div>
                        </FadeIn>
                    </div>

                    {/* Right: visual demo table */}
                    <FadeIn delay={150}>
                        <div className="glass rounded-2xl overflow-hidden shadow-xl">
                            {/* Table header */}
                            <div className="bg-surface-2/60 px-5 py-3.5 border-b border-white/[0.06] flex items-center gap-2">
                                <ShieldAlert size={14} className="text-red-400" />
                                <span className="text-sm font-medium text-white">扫描结果示例（5 个 SKU）</span>
                                <span className="ml-auto text-[10px] text-slate-500">对华税率估算</span>
                            </div>

                            <div className="divide-y divide-white/[0.04]">
                                {[
                                    { sku: 'SKU-001', name: '无线蓝牙降噪耳机', hts: '8518.30', rate: '145%', risk: 'danger' as const },
                                    { sku: 'SKU-002', name: '不锈钢保温水杯 32oz', hts: '7323.93', rate: '52.5%', risk: 'warning' as const },
                                    { sku: 'SKU-003', name: '帆布手提袋', hts: '4202.92', rate: '7.5%', risk: 'safe' as const },
                                    { sku: 'SKU-004', name: '儿童棉质 T恤', hts: '6109.10', rate: '45.6%', risk: 'warning' as const },
                                    { sku: 'SKU-005', name: 'USB-C 充电器 65W', hts: '8504.40', rate: '20%', risk: 'warning' as const },
                                ].map((row) => (
                                    <div key={row.sku} className="flex items-center gap-3 px-5 py-3">
                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${RISK_DOT[row.risk]}`} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs text-slate-300 truncate">{row.name}</p>
                                            <p className="text-[10px] text-slate-600 font-mono">{row.sku} · {row.hts}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <span className={`text-sm font-bold ${RISK_COLOR[row.risk]}`}>{row.rate}</span>
                                        </div>
                                        <div className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${RISK_BG[row.risk]} ${RISK_COLOR[row.risk]}`}>
                                            {RISK_LABEL[row.risk]}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Summary footer */}
                            <div className="bg-surface-2/40 px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
                                <div className="flex items-center gap-4 text-xs">
                                    <span className="flex items-center gap-1 text-red-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> 高风险 1
                                    </span>
                                    <span className="flex items-center gap-1 text-amber-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" /> 注意 3
                                    </span>
                                    <span className="flex items-center gap-1 text-emerald-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> 安全 1
                                    </span>
                                </div>
                                <span className="text-[10px] text-brand-400">导出 CSV 报告 →</span>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
}

// ── Section: Pain points ───────────────────────────────────────────────────────
function PainPoints() {
    const pains = [
        {
            emoji: '🏭',
            pain: '听说越南/墨西哥税率低，但不知道自己的产品具体能省多少，转产决策靠感觉',
            fix: '输入产品描述和进货成本，一键显示中越墨三国税率对比 + 含税利润差值，转产前先把账算清',
        },
        {
            emoji: '💥',
            pain: '选品入仓后才发现税率 145%，血亏退不了货',
            fix: '上架前批量预检全部 SKU，高风险商品即时推荐低税率替代来源，不再靠事后补救',
        },
        {
            emoji: '😤',
            pain: '手动查 HTSUS 官网：10 分钟找不到正确编码，还不确定税率对不对',
            fix: '输入产品描述，AI 3 秒内返回编码 + GRI 分类依据 + 多国税率，一步到位',
        },
        {
            emoji: '🤦',
            pain: '用 ChatGPT 查税率：训练数据可能过时，结果无法审计，没法当合规依据用',
            fix: '人工维护的实时税率数据库，每条分类记录含时间戳和 GRI 依据，可导出作为合规凭证',
        },
        {
            emoji: '💰',
            pain: '知道税率很高，但算不清含税后还能不能赚钱，定价全凭经验',
            fix: '输入进货成本，即时计算含税到岸成本和保住目标毛利的最低定价，选品有数字支撑',
        },
        {
            emoji: '💸',
            pain: '报关行报价 ¥200/SKU，千个 SKU 预算根本不够',
            fix: '批量上传 CSV，1,000 SKU 一次搞定，多国税率同步输出，成本降 90%+',
        },
    ];

    return (
        <section className="py-20 px-6" id="features">
            <div className="max-w-6xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs mb-4">
                            <AlertTriangle size={11} /> 你是否遇到过这些痛点？
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-3">报关分类，困扰每一个卖家</h2>
                        <p className="text-slate-400 max-w-xl mx-auto">从手动查表到 AI 辅助，税码通 解决的是真实的业务痛点</p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pains.map((item, i) => (
                        <FadeIn key={item.pain} delay={i * 60}>
                            <div className={`glass rounded-2xl p-5 h-full group hover:border-brand-500/30 transition-all duration-300 ${i === 0 ? 'ring-1 ring-red-500/20' : ''}`}>
                                <div className="text-2xl mb-3">{item.emoji}</div>
                                <div className="flex items-start gap-2 mb-3">
                                    <XCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-slate-400">{item.pain}</p>
                                </div>
                                <div className="flex items-start gap-2 pl-2 border-l-2 border-brand-600/40">
                                    <CheckCircle size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-white">{item.fix}</p>
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Section: How it works ──────────────────────────────────────────────────────
function HowItWorks() {
    const steps = [
        {
            n: '01',
            icon: FileText,
            title: '上传选品清单',
            desc: '粘贴商品描述，或上传 sku + description 格式的 CSV，最多 1,000 行，支持中英文描述。',
            color: 'from-brand-600 to-brand-500',
        },
        {
            n: '02',
            icon: Globe,
            title: 'AI 分类 + 多国税率并排',
            desc: 'Qwen / DeepSeek 识别 HTS 编码后，同步查询中国、越南、墨西哥、印度四国税率，含税到岸成本并排显示，最优货源自动标注。',
            color: 'from-violet-600 to-brand-600',
        },
        {
            n: '03',
            icon: TrendingDown,
            title: '迁产报告 + 利润决策一屏',
            desc: '各来源节省金额、含税利润对比、风险等级三合一呈现。高风险 SKU 推荐低税率替代来源，一键导出多来源对比报告，直接对接采购决策。',
            color: 'from-emerald-600 to-brand-600',
        },
    ];

    return (
        <section className="py-20 px-6 bg-surface-1/30">
            <div className="max-w-6xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-14">
                        <h2 className="text-3xl font-bold text-white mb-3">三步完成关税风险扫描</h2>
                        <p className="text-slate-400">从选品清单到风险报告，全程不超过 2 分钟</p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                    {/* Connector lines (desktop) */}
                    <div className="hidden md:block absolute top-12 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-brand-600/40 to-brand-600/40 pointer-events-none" />

                    {steps.map((step, i) => (
                        <FadeIn key={step.n} delay={i * 120}>
                            <div className="glass rounded-2xl p-7 text-center relative">
                                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto mb-5 shadow-lg`}>
                                    <step.icon size={22} className="text-white" />
                                </div>
                                <div className="text-xs font-bold text-slate-600 mb-2 font-mono">{step.n}</div>
                                <h3 className="text-base font-semibold text-white mb-3">{step.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Section: Feature grid ─────────────────────────────────────────────────────
function Features() {
    const features = [
        {
            icon: Globe,
            title: '多国货源关税对比',
            desc: '中国 / 越南 / 墨西哥 / 印度四国税率横向并排，USMCA、GSP 等 FTA 优惠自动标注，迁产前一眼看出哪国最划算。',
            badge: '核心差异化',
            color: 'text-brand-400',
            bg: 'bg-brand-500/10',
            highlight: true,
        },
        {
            icon: ShieldAlert,
            title: '选品关税风险扫描',
            desc: '批量识别高关税风险 SKU，三级风险标注（高/中/安全），高风险商品自动推荐低税率替代来源，可导出完整报告。',
            badge: '主力功能',
            color: 'text-red-400',
            bg: 'bg-red-500/10',
        },
        {
            icon: AlertTriangle,
            title: '关税数据实时维护',
            desc: '持续跟踪美国对华行政令、Section 301、FTA 变动，数据库每次政策更新后同步，不依赖 AI 训练截止日期。',
            badge: '数据优势',
            color: 'text-amber-400',
            bg: 'bg-amber-500/10',
        },
        {
            icon: UploadCloud,
            title: '批量 CSV 上传',
            desc: '单批次最多 1,000 个 SKU，支持 unit_cost 列自动触发利润测算。5 并发异步处理，完成后导出含税率 + 利润的完整报告。',
            badge: '效率工具',
            color: 'text-emerald-400',
            bg: 'bg-emerald-500/10',
        },
        {
            icon: Shield,
            title: '置信度 + 备选编码',
            desc: '每个结果都给出 0–100 置信度评分，并提供最多 3 个备选编码。置信度 < 75% 时主动建议专业复核。备选编码同时显示利润差值。',
            badge: '风险控制',
            color: 'text-violet-400',
            bg: 'bg-violet-500/10',
        },
        {
            icon: TrendingDown,
            title: '含税利润影响测算',
            desc: '输入进货成本，即时算出含税到岸成本和保住目标毛利的最低定价。支持全局成本 + 逐行覆盖，选品前先看利润，不再靠感觉定价。',
            badge: '独有功能',
            color: 'text-violet-400',
            bg: 'bg-violet-500/10',
            highlight: true,
        },
    ];

    return (
        <section className="py-20 px-6">
            <div className="max-w-6xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-14">
                        <h2 className="text-3xl font-bold text-white mb-3">功能一览</h2>
                        <p className="text-slate-400 max-w-lg mx-auto">专为跨境电商卖家和货代设计，覆盖从选品风险识别到批量报关分类的全流程</p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {features.map((f, i) => {
                        const isRiskHighlight = f.highlight && f.color.includes('red');
                        const isProfitHighlight = f.highlight && !f.color.includes('red');
                        return (
                            <FadeIn key={f.title} delay={i * 60}>
                                <div className={`glass rounded-2xl p-6 h-full hover:border-white/15 transition-all duration-300 ${isRiskHighlight ? 'ring-1 ring-red-500/25 bg-red-500/5' : ''} ${isProfitHighlight ? 'ring-1 ring-violet-500/25 bg-violet-500/5' : ''}`}>
                                    <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                                        <f.icon size={18} className={f.color} />
                                    </div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-sm font-semibold text-white">{f.title}</h3>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isRiskHighlight ? 'text-red-400 border-red-500/30 bg-red-500/10' : isProfitHighlight ? 'text-violet-400 border-violet-500/30 bg-violet-500/10' : 'text-slate-500 bg-surface-2 border-white/[0.06]'}`}>
                                            {f.badge}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                                </div>
                            </FadeIn>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ── Section: Comparison table ──────────────────────────────────────────────────
function CompareTable() {
    const rows = [
        { feature: '多来源关税对比（CN/VN/MX/IN 并排）', hs: true, usitc: false, gpt: false, pro: false },
        { feature: 'SKU 级含税利润 + 迁产节省测算', hs: true, usitc: false, gpt: false, pro: false },
        { feature: '选品关税风险扫描 + 替代来源建议', hs: true, usitc: false, gpt: false, pro: false },
        { feature: 'AI 自动识别 HTS 编码', hs: true, usitc: false, gpt: 'partial', pro: true },
        { feature: '10位完整编码 + GRI 推理可溯源', hs: true, usitc: true, gpt: false, pro: true },
        { feature: '置信度评分 + 备选编码', hs: true, usitc: false, gpt: false, pro: true },
        { feature: '关税数据实时维护（非 AI 训练截止）', hs: true, usitc: false, gpt: false, pro: 'partial' },
        { feature: '批量 CSV 处理（≥100 SKU）', hs: true, usitc: false, gpt: false, pro: true },
        { feature: '免费起步', hs: true, usitc: true, gpt: 'partial', pro: false },
        { feature: '中文界面 + 中文描述支持', hs: true, usitc: false, gpt: true, pro: 'partial' },
        { feature: 'API 集成 / ERP 对接', hs: 'roadmap', usitc: false, gpt: false, pro: true },
    ];

    const cols = [
        { key: 'hs', label: '税码通', highlight: true },
        { key: 'usitc', label: 'USITC 官网' },
        { key: 'gpt', label: 'ChatGPT 类' },
        { key: 'pro', label: '专业报关软件' },
    ];

    const Icon = ({ v }: { v: boolean | 'partial' | 'roadmap' }) => {
        if (v === true) return <CheckCircle size={16} className="text-emerald-400 mx-auto" />;
        if (v === false) return <XCircle size={16} className="text-red-400/50 mx-auto" />;
        if (v === 'partial') return <span className="text-amber-400 text-xs mx-auto block text-center">部分</span>;
        return <span className="text-slate-500 text-xs mx-auto block text-center">规划中</span>;
    };

    return (
        <section className="py-20 px-6 bg-surface-1/30" id="compare">
            <div className="max-w-5xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-white mb-3">和竞品有什么不同？</h2>
                        <p className="text-slate-400">别被通用工具误导，对比一下真正的差距</p>
                    </div>
                </FadeIn>

                <FadeIn delay={100}>
                    <div className="glass rounded-2xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-white/[0.06]">
                                    <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-1/3">功能</th>
                                    {cols.map((col) => (
                                        <th key={col.key} className={`px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide ${col.highlight ? 'text-brand-400 bg-brand-500/5' : 'text-slate-500'}`}>
                                            {col.label}
                                            {col.highlight && <Star size={10} className="inline ml-1 mb-0.5 fill-current" />}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, i) => (
                                    <tr key={row.feature} className={`border-b border-white/[0.04] ${i === 0 ? 'bg-brand-500/5' : i === 1 ? 'bg-violet-500/5' : i === 2 ? 'bg-red-500/5' : i % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
                                        <td className={`px-5 py-3 text-xs ${i === 0 ? 'text-brand-300 font-medium' : i === 1 ? 'text-violet-300 font-medium' : i === 2 ? 'text-red-300 font-medium' : 'text-slate-300'}`}>
                                            {i === 0 && <Globe size={11} className="inline mr-1.5 text-brand-400" />}
                                            {i === 1 && <TrendingDown size={11} className="inline mr-1.5 text-violet-400" />}
                                            {i === 2 && <ShieldAlert size={11} className="inline mr-1.5 text-red-400" />}
                                            {row.feature}
                                        </td>
                                        {cols.map((col) => (
                                            <td key={col.key} className={`px-4 py-3 ${col.highlight ? 'bg-brand-500/5' : ''}`}>
                                                <Icon v={row[col.key as keyof typeof row] as boolean | 'partial' | 'roadmap'} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

// ── Section: Pricing ───────────────────────────────────────────────────────────
function Pricing() {
    const plans = [
        {
            name: 'Free',
            nameZh: '免费版',
            price: '¥0',
            period: '/月',
            desc: '个人卖家 / 小规模测试',
            cta: '免费注册',
            ctaTo: '/register',
            highlight: false,
            features: [
                '50 次单品分类 / 月',
                '风险扫描 ≤20 SKU / 批',
                '批量上传 ≤50 SKU / 批',
                '历史记录保存 30 天',
                '置信度 + 备选编码',
            ],
            limits: ['无扫描报告导出', '无优先队列'],
        },
        {
            name: 'Pro',
            nameZh: '专业版',
            price: '¥299',
            period: '/月',
            desc: '中型卖家 / 货代',
            cta: '开始 7 天免费试用',
            ctaTo: '/register',
            highlight: true,
            badge: '最受欢迎',
            features: [
                '1,000 次单品分类 / 月',
                '风险扫描 ≤1,000 SKU / 批',
                '风险报告导出（CSV + 优化建议）',
                '批量上传 ≤1,000 SKU / 批',
                '历史记录永久保存',
                '2025 对华税率实时估算',
                '优先处理队列',
                'Email 支持',
            ],
            limits: [],
        },
        {
            name: 'Enterprise',
            nameZh: '企业版',
            price: '面议',
            period: '',
            desc: '大型电商平台 / 报关行',
            cta: '联系我们',
            ctaTo: '/register',
            highlight: false,
            features: [
                '无限分类 + 无限风险扫描',
                'REST API 访问',
                'Webhook / ERP 集成',
                '自定义税率规则',
                '专属客户成功经理',
                'SLA 保障 + 发票',
                '私有化部署可选',
            ],
            limits: [],
        },
    ];

    return (
        <section className="py-20 px-6" id="pricing">
            <div className="max-w-5xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-14">
                        <h2 className="text-3xl font-bold text-white mb-3">透明定价，按需选择</h2>
                        <p className="text-slate-400">所有套餐均包含核心 AI 分类能力，Pro 版解锁完整风险扫描报告</p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {plans.map((plan, i) => (
                        <FadeIn key={plan.name} delay={i * 80}>
                            <div className={`rounded-2xl p-6 h-full flex flex-col relative overflow-hidden ${
                                plan.highlight
                                    ? 'bg-brand-600/15 border-2 border-brand-500/40 shadow-xl shadow-brand-900/30'
                                    : 'glass'
                            }`}>
                                {plan.badge && (
                                    <div className="absolute top-0 right-0 bg-brand-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                                        {plan.badge}
                                    </div>
                                )}

                                <div className="mb-5">
                                    <div className="text-xs text-slate-500 mb-1">{plan.nameZh}</div>
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="text-3xl font-bold text-white">{plan.price}</span>
                                        <span className="text-slate-400 text-sm">{plan.period}</span>
                                    </div>
                                    <p className="text-xs text-slate-500">{plan.desc}</p>
                                </div>

                                <ul className="space-y-2.5 flex-1 mb-6">
                                    {plan.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-xs">
                                            <CheckCircle size={13} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-300">{f}</span>
                                        </li>
                                    ))}
                                    {plan.limits.map((l) => (
                                        <li key={l} className="flex items-start gap-2 text-xs">
                                            <XCircle size={13} className="text-slate-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-600">{l}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    to={plan.ctaTo}
                                    className={`w-full text-center text-sm font-medium py-2.5 rounded-xl transition-all ${
                                        plan.highlight
                                            ? 'btn-primary'
                                            : 'btn-secondary'
                                    }`}
                                >
                                    {plan.cta}
                                </Link>
                            </div>
                        </FadeIn>
                    ))}
                </div>

                <FadeIn delay={200}>
                    <p className="text-center text-xs text-slate-600 mt-6">
                        价格以人民币计，亦支持美元结算。Pro 版首月 7 天免费试用，随时可取消。
                    </p>
                </FadeIn>
            </div>
        </section>
    );
}

// ── Section: FAQ ───────────────────────────────────────────────────────────────
function FAQ() {
    const [open, setOpen] = useState<number | null>(null);

    const faqs = [
        {
            q: '多国货源对比功能怎么用？',
            a: '上传包含 sku 和 description 的 CSV（可加 unit_cost 列），扫描完成后每个 SKU 下方会展示中国、越南、墨西哥、印度四国的税率对比和含税到岸成本差值。若有进货成本，还会直接显示「迁至 X 国可节省 $Y/件」。可一键导出含多国对比的完整报告。',
        },
        {
            q: '越南、墨西哥的税率数据准确吗？',
            a: '越南对美出口适用美国 MFN（最惠国）税率，我们持续维护 USITC 官方税率数据库；墨西哥通过 USMCA 对大多数商品享有 0% 关税（需满足原产地规则）。系统会标注 FTA 适用情况。税率数据定期更新，建议以海关核定为准。',
        },
        {
            q: '墨西哥 USMCA 0% 税率是所有商品都适用吗？',
            a: '不是所有商品都自动适用。USMCA 要求满足「原产地规则」（Rules of Origin），通常要求在北美区域内有足够的制造增值。大多数工业品、服装、电子品有具体的 ROO 规定。税码通目前提供简化版资格预检（标注「可能符合/需要核查」），复杂情况建议咨询报关行确认。',
        },
        {
            q: 'AI 分类的准确率有多高？',
            a: '常见消费品和电子产品的分类置信度通常在 85–95%。置信度低于 75% 时，系统会主动提示并建议向报关行确认。平台会持续通过用户反馈迭代优化。',
        },
        {
            q: '进货成本和利润测算功能怎么用？',
            a: 'CSV 中加入 unit_cost（或 cost/cogs）列，扫描完成后自动计算各来源国的含税到岸成本和保住目标毛利（默认 30%）的最低定价。也可在结果页手动开启利润测算模式，全局输入或逐行覆盖成本。',
        },
        {
            q: '直接用 ChatGPT 查税率不行吗？',
            a: '单条查询可以，但有三个核心缺口：① 多国税率对比 — ChatGPT 不会自动帮你做 CN/VN/MX 横向比较；② 数据时效性 — AI 训练截止日期不保证包含最新政策变化，我们实时维护数据库；③ 批量工作流 — 100 个 SKU 逐条粘贴给 AI 需要几小时，税码通 CSV 一次搞定。',
        },
        {
            q: 'CSV 批量上传需要什么格式？',
            a: 'CSV 文件至少需要 description 列，sku 和 unit_cost 列可选。可在扫描页下载模板（含多国对比和利润测算的完整格式）。单批次最多 1,000 行，大小限制 10MB。',
        },
        {
            q: '分类结果可以用于正式报关吗？',
            a: '税码通的结果是辅助决策工具，不构成法律建议。高置信度（≥80%）的结果可作为初步判断依据，正式报关建议由持证报关员最终确认，特别是涉及 FTA 资格认定的场景。',
        },
    ];

    return (
        <section className="py-20 px-6 bg-surface-1/30" id="faq">
            <div className="max-w-3xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-white mb-3">常见问题</h2>
                        <p className="text-slate-400">还有问题？欢迎发邮件或通过 GitHub 提 Issue</p>
                    </div>
                </FadeIn>

                <div className="space-y-3">
                    {faqs.map((faq, i) => (
                        <FadeIn key={i} delay={i * 40}>
                            <div
                                className="glass rounded-xl overflow-hidden cursor-pointer"
                                onClick={() => setOpen(open === i ? null : i)}
                            >
                                <div className="flex items-center justify-between px-5 py-4">
                                    <h3 className="text-sm font-medium text-white">{faq.q}</h3>
                                    <ChevronDown
                                        size={16}
                                        className={`text-slate-400 flex-shrink-0 ml-4 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
                                    />
                                </div>
                                {open === i && (
                                    <div className="px-5 pb-4 border-t border-white/[0.04]">
                                        <p className="text-sm text-slate-400 leading-relaxed pt-3">{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Section: Final CTA ─────────────────────────────────────────────────────────
function FinalCTA() {
    const { isAuthenticated } = useAuth();
    return (
        <section className="py-24 px-6">
            <div className="max-w-3xl mx-auto text-center">
                <FadeIn>
                    <div className="relative">
                        <div className="absolute inset-0 bg-brand-600/10 rounded-3xl blur-3xl" />
                        <div className="relative glass rounded-3xl p-12 border border-brand-500/20">
                            <div className="text-4xl mb-4">🛡️</div>
                            <h2 className="text-3xl font-bold text-white mb-4">
                                {isAuthenticated ? '对比货源，知道利润，再决定去哪生产' : '迁产前先算清楚，别让错误决策浪费半年时间'}
                            </h2>
                            <p className="text-slate-400 mb-8 leading-relaxed">
                                {isAuthenticated
                                    ? '进入扫描页，上传含进货成本的 SKU 清单，立即获得中越墨三国税率对比 + 含税利润分析报告。'
                                    : <>免费注册，无需信用卡。50 次免费分类 + 多来源对比 + 利润测算，<br />把迁产方案的数据账算清楚再做决策。</>
                                }
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                {isAuthenticated ? (
                                    <>
                                        <Link to="/scan" className="btn-primary text-base px-8 py-3">
                                            <ShieldAlert size={18} />
                                            立即扫描选品
                                            <ArrowRight size={16} />
                                        </Link>
                                        <Link to="/dashboard" className="btn-secondary text-base px-8 py-3">
                                            <LayoutDashboard size={16} />
                                            进入控制台
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Link to="/register" className="btn-primary text-base px-8 py-3">
                                            <ShieldAlert size={18} />
                                            免费扫描选品风险
                                            <ArrowRight size={16} />
                                        </Link>
                                        <a
                                            href="mailto:hello@shuimatong.com"
                                            className="btn-secondary text-base px-8 py-3"
                                        >
                                            <Globe size={16} />
                                            联系我们
                                        </a>
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-slate-600 mt-6">
                                已有超过 200 位卖家和货代在使用 · 平均每次分类节省 8 分钟
                            </p>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

// ── Footer ─────────────────────────────────────────────────────────────────────
function Footer() {
    return (
        <footer className="border-t border-white/[0.06] py-10 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-3">
                        <LogoIcon size={28} />
                        <div>
                            <span className="text-sm font-bold text-white">税码通</span>
                            <span className="text-xs text-slate-600 ml-2">供应链多元化决策平台 · 多国货源对比 + HTS 分类 + 利润测算</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 text-xs text-slate-500">
                        <a href="#risk-scan" className="hover:text-slate-300 transition-colors">风险扫描</a>
                        <a href="#features" className="hover:text-slate-300 transition-colors">功能</a>
                        <a href="#compare" className="hover:text-slate-300 transition-colors">对比</a>
                        <a href="#pricing" className="hover:text-slate-300 transition-colors">定价</a>
                        <a href="#faq" className="hover:text-slate-300 transition-colors">FAQ</a>
                        <Link to="/login" className="hover:text-slate-300 transition-colors">登录</Link>
                        <Link to="/register" className="hover:text-slate-300 transition-colors">注册</Link>
                    </div>

                    <p className="text-xs text-slate-600">
                        © 2026 税码通 · 税率数据仅供参考，以海关核定为准
                    </p>
                </div>
            </div>
        </footer>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function LandingPage() {
    return (
        <div className="min-h-screen">
            <Navbar />
            <Hero />
            <TariffBanner />
            <Stats />
            <RiskScanShowcase />
            <PainPoints />
            <HowItWorks />
            <Features />
            <CompareTable />
            <Pricing />
            <FAQ />
            <FinalCTA />
            <Footer />
        </div>
    );
}
